import React, { FC, useRef, useEffect, useState, memo, useMemo } from 'react';
import * as eCharts from 'echarts';
import { lineTooltipFormatter } from './linechart';
import { useIntl, getLocale } from 'umi';
import { ChartType } from '@intelligence/pages/TopCharts/_config/enums';
import usePieChartsHoverEffect from '@intelligence/components/Chart/hooks/usePieChartsHoverEffect';
import { getPieChartLabelBaseRich } from '@intelligence/utils/echartsUtils';
import { PIE_CHART_TOOLTIP_BASE_OPTION } from '@intelligence/utils/echartsConst';
import { reduce, reduceRight } from 'lodash-es';

interface RegionalPieChartProps {
  pieData: any[];
  segment: string;
  levelValue: any;
  trendsSegment: string;
}

const color = [
  '#376AE7',
  '#3ECBFF',
  '#4FCB87',
  '#FFD14E',
  '#ED9142',
  '#E95F6A',
  '#CF61AC',
  '#8164CD',
  '#6585F7',
  '#A0B6FA',
  '#f19e55',
  '#69d19c',
];

const RegionalPieChart: FC<RegionalPieChartProps> = (props) => {
  let myChart: any = null;
  const { pieData = [], segment, levelValue, trendsSegment } = props;
  const { formatMessage } = useIntl();
  const locale = getLocale();
  const chartRef = useRef<any>(null);
  const [options, setOptions] = useState<any>();

  const pieDataList = useMemo(() => {
    return pieData?.map((item, index) => {
      return {
        ...item,
        name: locale === 'zh-CN' ? item?.name_zh : item?.name,
        seriesName: locale === 'zh-CN' ? item?.name_zh : item?.name,
        color: color[index],
      };
    });
  }, [pieData, locale]);

  const pieChartInstance = useRef<any>(null);
  const chartsInstance = useRef<any>({
    getPieInstance: () => {
      return pieChartInstance?.current;
    },
  });
  const [loading, setLoading] = useState(true);
  usePieChartsHoverEffect({
    loading,
    chartType: ChartType.Pie,
    chartsInstance,
    setOption: setOptions,
  });

  const levelTitle = useMemo(() => {
    if (levelValue) {
      const levelList = reduce(
        levelValue,
        (result, value, key) => {
          if (value?.length) {
            result.push(key);
          }
          return result;
        },
        [] as any,
      );

      const targetLevelList = levelList?.sort((a: any, b: any) => {
        const aCharCodeAt =
          a?.data?.level === 'S' ? 64 : a?.data?.level?.charCodeAt();
        const bCharCodeAt =
          b?.data?.level === 'S' ? 64 : b?.data?.level?.charCodeAt();

        return aCharCodeAt - bCharCodeAt;
      });

      return targetLevelList?.join('/');
    }
    return '';
  }, [locale, levelValue]);

  const gameText = useMemo(()=> {
    if(locale === 'zh-CN'){
      if(segment === 'customize_mobile'){
        return  {
          text: `定制游戏\n 级别 ${levelTitle}`,
          top: '45%'
        }
      }
      return {
        text: `${formatMessage({id: `dbi_benchmark_game_${segment}`})}游戏\n 级别 ${levelTitle}`,
        top: '45%'
      }
    }else{
      if(segment === 'shooter'){
        return {
          text: `SHOOTER\nGAME/\nLEVEL ${levelTitle}`,
          top: '42%'
        } 
      }
      if(segment === 'customize_mobile'){
        return {
          text:  `CUSTOMIZED\nGAME/\nLEVEL ${levelTitle}`,
          top: '42%'
        }
      }
      return {
        text: `${segment.toLocaleUpperCase()} GAME/\nLEVEL ${levelTitle}`,
        top: '45%'
      } ;
    }
   
  }, [segment, levelTitle, locale])

  useEffect(() => {
    const gameLineOptions = {
      color: color,
      graphic: {
        type: 'text',
        left: 'center',
        top: gameText?.top || 0,
        style: {
          text: gameText?.text || '',
          textAlign: 'center',
          fill: '#262832',
          fontSize: 12,
          fontWeight: 'bold',
        },
      },
      grid: {},
      legend: {
        type: 'scroll',
        orient: 'horizontal',
        pageIconColor: '#ff781f',
        selectedMode: true,
        pageIconSize: 13,
        pageTextStyle: {
          color: '#999',
        },
        show: true,
        icon: 'circle',
        itemWidth: 20,
        itemHeight: 10,
        left: 'center',
        bottom: -4,
        textStyle: {
          color: '#888888',
          fontSize: 12,
        },
      },
      tooltip: {
        ...PIE_CHART_TOOLTIP_BASE_OPTION,
        show: true,
        confine: true,
        showDelay: 10,
        hideDelay: 100,
        extraCssText: 'border: none;',
        formatter: (param: any) => {
          return lineTooltipFormatter(
            pieDataList,
            // formatMessage({ id: 'dbi_benchmark_players_proportion' }),
            formatMessage({ id: `dbi_benchmark_${trendsSegment}_mobile` }),
            '%',
            (value: any, data: any, target: any) => {
              return `${(target?.dod * 100)?.toFixed(2)}%` || '';
            },
          );
        },
        backgroundColor: 'rgba(0, 0, 0, 0)',
      },
      series: [
        {
          data: pieDataList,
          type: 'pie',
          // radius: ['45px', '65px'],
          radius: [50, 70],
          avoidLabelOverlap: true,
          startAngle: 180,
          itemStyle: {
            borderRadius: 5,
            borderColor: '#fff',
            borderWidth: 1,
          },
          label: {
            normal: {
              padding: [0, -6],
              fontSize: 12,
              color: '#262832',
              opacity: 1,
              rich: {
                ...getPieChartLabelBaseRich(),
              },
              // formatter: formatPieChartLabel,
              formatter: (params) => {
                const name = params?.name;
                const percent =
                  params?.percent === undefined ? 0 : params?.percent;
                return `${name}:  {per|${
                  percent?.toFixed(2) || percent
                }%}\n{hr|}\n{a|}`;
              },
            },
          },
          labelLine: {
            // 折线弯曲程度配置
            length: 20,
            showAbove: false,
            // minTurnAngle: 80,
            maxSurfaceAngle: 80,
            lineStyle: {
              width: 2,
            },
          },
          name: 'Access From',
          minAngle: 5,
          bottom: 20,
        },
      ],
    };

    setOptions(gameLineOptions);
  }, [pieData, locale, levelValue]);

  const handleResize = () => {
    try {
      myChart.resize();
    } catch (err) {}
  };

  useEffect(() => {
    if (myChart === null) {
      myChart = eCharts.init(chartRef!.current!);
      pieChartInstance.current = myChart;
      setLoading(false);
    }
    options && myChart.setOption(options);
  }, [options]);

  useEffect(() => {
    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, [myChart]);

  return <div ref={chartRef} style={{ height: 288, width: '100%' }}></div>;
};

RegionalPieChart.defaultProps = {
  pieData: [],
};

export default memo(RegionalPieChart);
